import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChapterDetail = () => {
  const { slug } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [allChapters, setAllChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChapter();
    fetchAllChapters();
  }, [slug]);

  const fetchChapter = async () => {
    try {
      const response = await axios.get(`${API_URL}/chapters/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChapter(response.data);
      
      // Tracker la lecture du chapitre pour la gamification
      try {
        await axios.post(`${API_URL}/gamification/track-chapter/${slug}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        // Silencieux si erreur de tracking
      }
    } catch (error) {
      console.error('Erreur chargement chapitre:', error);
      toast.error('Erreur lors du chargement du chapitre');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllChapters = async () => {
    try {
      const response = await axios.get(`${API_URL}/chapters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllChapters(response.data);
    } catch (error) {
      console.error('Erreur chargement chapitres:', error);
    }
  };

  const toggleFavorite = async () => {
    try {
      await axios.post(`${API_URL}/chapters/${chapter.id}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChapter({ ...chapter, is_favorite: !chapter.is_favorite });
    } catch (error) {
      console.error('Erreur toggle favori:', error);
    }
  };

  // Navigation entre chapitres
  const currentIndex = allChapters.findIndex(c => c.slug === slug);
  const prevChapter = currentIndex > 0 ? allChapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < allChapters.length - 1 ? allChapters[currentIndex + 1] : null;

  const goToChapter = (chapterSlug) => {
    window.scrollTo(0, 0);
    navigate(`/methode/${chapterSlug}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-6">
        <p className="text-sage-700 font-sans mb-4">Chapitre non trouvé</p>
        <Button onClick={() => navigate('/methode')}>Retour</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="chapter-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-sand-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/methode')}
            data-testid="chapter-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          {/* Espace pour la clochette de notification (positionnée en fixed) + bouton favori */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFavorite}
              data-testid="chapter-detail-favorite-button"
            >
              <Heart
                className={`w-5 h-5 ${
                  chapter.is_favorite
                    ? 'text-declic fill-declic'
                    : 'text-sage-700/40'
                }`}
              />
            </Button>
            {/* Espace réservé pour la clochette */}
            <div className="w-10" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <span className="text-sm font-sans text-sage-700/60">
          Déclic {chapter.index}
        </span>
        <h1 className="text-3xl font-serif font-light text-sage-700 mt-2 mb-6" data-testid="chapter-detail-title">
          {chapter.title}
        </h1>

        <div className="markdown-content prose-sage">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{chapter.content}</ReactMarkdown>
        </div>
      </div>

      {/* Navigation entre chapitres */}
      <div className="px-6 py-6 border-t border-sand-200 bg-sand-50">
        <div className="flex flex-col gap-3">
          {prevChapter && (
            <button
              onClick={() => goToChapter(prevChapter.slug)}
              className="flex items-center gap-3 p-3 bg-white rounded-xl border border-sand-200 hover:border-sage-300 transition-colors text-left"
              data-testid="prev-chapter-btn"
            >
              <ChevronLeft className="w-5 h-5 text-sage-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-sage-500">Précédent</p>
                <p className="text-sm font-medium text-sage-700 truncate">
                  {prevChapter.title.replace(/^\d+\)\s*/, '')}
                </p>
              </div>
            </button>
          )}

          {nextChapter && (
            <button
              onClick={() => goToChapter(nextChapter.slug)}
              className="flex items-center gap-3 p-3 bg-white rounded-xl border border-sand-200 hover:border-sage-300 transition-colors text-left"
              data-testid="next-chapter-btn"
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs text-sage-500">Suivant</p>
                <p className="text-sm font-medium text-sage-700 truncate">
                  {nextChapter.title.replace(/^\d+\)\s*/, '')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-sage-500 flex-shrink-0" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChapterDetail;